import { Component, inject, HostListener } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NavigationService } from '../../../core/services/navigation';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule],
  template: `
    <header class="bg-slate-600 text-white shadow-lg fixed top-0 left-0 right-0 z-50">
      <div class="container mx-auto px-4">
        <div class="flex items-center justify-between h-16">
          <!-- Logo/Title -->
          <a routerLink="/" class="flex items-center space-x-2">
            <img
              src="images/skos_logo.png"
              alt="SKO Süd Logo"
              class="h-12 w-auto"
            />
            <div class="hidden sm:block">
              <h1 class="text-lg font-bold">Schützenkreis</h1>
              <p class="text-xs text-slate-200">Oberschwaben-Süd</p>
            </div>
          </a>

          <!-- Desktop Navigation -->
          <nav class="hidden md:flex space-x-6">
            @for (item of navService.navItems; track item.label; let isLast = $last) {
              @if (item.children) {
                <!-- Dropdown Menu -->
                <div class="relative dropdown-container">
                  <button
                    (click)="navService.toggleDropdown(item.label); $event.stopPropagation()"
                    class="py-2 hover:text-slate-200 transition-colors flex items-center gap-1">
                    {{ item.label }}
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>

                  @if (navService.openDropdown() === item.label) {
                    <div [class]="isLast ? 'absolute top-full right-0 mt-1 w-56 bg-white rounded-lg shadow-lg py-2 z-50' : 'absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg py-2 z-50'">
                      @for (child of item.children; track child.label) {
                        @if (child.route) {
                          <a [routerLink]="child.route"
                             (click)="navService.closeDropdown()"
                             class="block px-4 py-2 text-slate-700 hover:bg-slate-100 transition-colors">
                            {{ child.label }}
                          </a>
                        } @else if (child.externalUrl) {
                          <a [href]="child.externalUrl"
                             target="_blank"
                             rel="noopener noreferrer"
                             class="block px-4 py-2 text-slate-700 hover:bg-slate-100 transition-colors">
                            {{ child.label }}
                            <svg class="w-3 h-3 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                          </a>
                        }
                      }
                    </div>
                  }
                </div>
              } @else {
                <!-- Regular Link -->
                <a [routerLink]="item.route"
                   routerLinkActive="border-b-2 border-white"
                   [routerLinkActiveOptions]="{exact: item.route === '/'}"
                   class="py-2 hover:text-slate-200 transition-colors">
                  {{ item.label }}
                </a>
              }
            }
          </nav>

          <!-- Mobile Menu Button -->
          <button
            (click)="navService.toggleMobileMenu()"
            class="md:hidden p-2 rounded-lg hover:bg-slate-700 transition-colors"
            [attr.aria-label]="navService.isMobileMenuOpen() ? 'Close menu' : 'Open menu'">
            @if (!navService.isMobileMenuOpen()) {
              <!-- Hamburger Icon -->
              <svg class="w-6 h-6"
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            } @else {
              <!-- Close Icon -->
              <svg class="w-6 h-6"
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"/>
              </svg>
            }
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      @if (navService.isMobileMenuOpen()) {
        <nav class="md:hidden border-t border-slate-500 bg-slate-700 mobile-nav">
          <div class="container mx-auto px-4 py-2">

            @for (item of navService.navItems; track item.label) {
              @if (item.children) {
                <!-- Mobile Dropdown -->
                <div class="dropdown-container">
                  <button
                    (click)="navService.toggleDropdown(item.label); $event.stopPropagation()"
                    class="w-full text-left py-3 px-4 rounded-lg hover:bg-slate-600 transition-colors flex items-center justify-between">
                    <span>{{ item.label }}</span>
                    <svg class="w-4 h-4 transition-transform"
                         [class.rotate-180]="navService.openDropdown() === item.label"
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>

                  @if (navService.openDropdown() === item.label) {
                    <div class="bg-slate-800 rounded-lg mt-1 py-1">
                      @for (child of item.children; track child.label) {
                        @if (child.route) {
                          <a [routerLink]="child.route"
                             (click)="navService.closeMobileMenu()"
                             class="block py-2 px-6 hover:bg-slate-700 transition-colors">
                            {{ child.label }}
                          </a>
                        } @else if (child.externalUrl) {
                          <a [href]="child.externalUrl"
                             target="_blank"
                             rel="noopener noreferrer"
                             class="block py-2 px-6 hover:bg-slate-700 transition-colors">
                            {{ child.label }} ↗
                          </a>
                        }
                      }
                    </div>
                  }
                </div>
              } @else {
                <!-- Mobile Regular Link -->
                <a [routerLink]="item.route"
                   (click)="navService.closeMobileMenu()"
                   routerLinkActive="bg-slate-800"
                   [routerLinkActiveOptions]="{exact: item.route === '/'}"
                   class="block py-3 px-4 rounded-lg hover:bg-slate-600 transition-colors">
                  {{ item.label }}
                </a>
              }
            }
          </div>
        </nav>
      }
    </header>
  `
})
export class Header {
  navService = inject(NavigationService);

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Don't close if clicking inside mobile menu or dropdown
    if (!target.closest('.dropdown-container') && !target.closest('.mobile-nav')) {
      this.navService.closeDropdown();
    }
  }

  // Debug helper
  debug(message: string, value?: any) {
    console.log(message, value);
  }
}
